import ButtonTemplate from '../../html/components/button.html'
import ButtonGroupTemplate from '../../html/components/button-group.html'
import ButtonToolbarTemplate from '../../html/components/button-toolbar.html'

import {inert} from '../shared/directives'
import Component from '../shared/component'
import Relation, {HasMany, MemberOf} from '../shared/mixins/relation'

import {Interactive, InteractiveMixin} from './interactive'
import {PopoverToggleMixin} from './popover'
import {TooltipToggleMixin} from './tooltip'
import {CollapseToggleMixin} from './collapse'
import {DropdownToggleMixin} from './dropdown'

const buttonGroup = Relation({explicit: true})

export const Button = Component('v-button', {
  components: [
    Interactive
  ],
  mixins: [
    InteractiveMixin,
    PopoverToggleMixin,
    TooltipToggleMixin,
    CollapseToggleMixin,
    DropdownToggleMixin,
    MemberOf(buttonGroup, 'group')
  ],
  directives: {
    inert
  },
  template: ButtonTemplate,
  props: {
    variant: {
      type: String,
      default: null,
      valid: [
        'outline',
        'primary',
        'success',
        'danger'
      ]
    },
    type: {
      type: String,
      default: 'button',
      valid: [
        'submit',
        'reset',
        'button',
        'menu'
      ]
    },
    disabled: {
      type: Boolean,
      default: false
    },
    selected: {
      type: Boolean,
      default: false
    },
    busy: {
      type: Boolean,
      default: false
    },
    block: {
      type: Boolean,
      default: false
    },
    borderless: {
      type: Boolean,
      default: false
    }
  },
  computed: {
    listeners () {
      if (this.busy) {
        return {...this.$listeners, click: () => null}
      } else {
        return this.$listeners
      }
    },

    classes () {
      return {
        [`is-${this.variant}`]: this.variant,
        'is-selected': this.selected,
        'is-disabled': this.disabled || this.busy,
        'is-block': this.block,
        'is-borderless': this.borderless,
        'is-clicked': this.clicked,
        'has-group': !!this.group,
        'is-first': this.first,
        'is-last': this.last
      }
    },

    describedBy () {
      const {$attrs} = this

      if ($attrs) {
        return $attrs['aria-describedby']
      }
    },

    expanded () {
      const toggles = ['popover', 'collapse', 'dropdown']

      for (let i = 0, n = toggles.length; i < n; i++) {
        const toggle = this[toggles[i]]

        if (toggle) {
          return toggle.active
        }
      }

      const {$attrs} = this

      if ($attrs) {
        return $attrs['aria-expanded']
      }
    },

    controls () {
      const toggles = ['popover', 'collapse', 'dropdown']

      for (let i = 0, n = toggles.length; i < n; i++) {
        const toggle = this[toggles[i]]

        if (toggle) {
          return toggle.target
        }
      }

      const {$attrs} = this

      if ($attrs) {
        return $attrs['aria-controls']
      }
    },

    clicked () {
      const toggles = ['popover', 'dropdown']

      for (let i = 0, n = toggles.length; i < n; i++) {
        const toggle = this[toggles[i]]

        if (toggle) {
          return toggle.active
        }
      }
    },

    first () {
      const {group} = this

      if (group) {
        return group.first === this.id
      }
    },

    last () {
      const {group} = this

      if (group) {
        return group.last === this.id
      }
    }
  }
})

export const ButtonGroup = Component('v-button-group', {
  mixins: [
    HasMany(buttonGroup, 'buttons')
  ],
  template: ButtonGroupTemplate,
  props: {
    label: {
      type: String,
      default: null
    }
  },
  computed: {
    owns () {
      return this.buttons.map(button => button.id).join(' ')
    },

    first () {
      if (this.buttons.length) {
        return this.buttons[0].id
      }
    },

    last () {
      if (this.buttons.length) {
        return this.buttons[this.buttons.length - 1].id
      }
    }
  }
})

export const ButtonToolbar = Component('v-button-toolbar', {
  template: ButtonToolbarTemplate,
  props: {
    label: {
      type: String,
      default: null,
      required: true
    }
  }
})
